'use client'

import {
  FocusEvent,
  KeyboardEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'
import Image from 'next/image'
import { SelectProps } from './types'

export function Select({
  id,
  name,
  label,
  options,
  value,
  defaultValue = '',
  required,
  disabled,
  className = '',
  onChange,
  onClear,
  ...props
}: SelectProps) {
  const generatedId = useId()
  const fieldId = id ?? `select-${generatedId}`
  const labelId = `${fieldId}-label`
  const listboxId = `${fieldId}-listbox`
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [internalValue, setInternalValue] = useState(defaultValue)
  const currentValue = value ?? internalValue
  const selectedIndex = options.findIndex((option) => option.value === currentValue)
  const [activeIndex, setActiveIndex] = useState(selectedIndex >= 0 ? selectedIndex : 0)

  useEffect(() => {
    function handlePointerDown(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
    }
  }, [])

  const selectedOption = options.find((option) => option.value === currentValue)
  const hasValue = Boolean(selectedOption)
  const activeOptionId = isOpen ? `${fieldId}-option-${activeIndex}` : undefined

  function openListbox(nextActiveIndex = selectedIndex >= 0 ? selectedIndex : 0) {
    if (disabled || options.length === 0) {
      return
    }

    setActiveIndex(nextActiveIndex)
    setIsOpen(true)
  }

  function selectOption(nextValue: string) {
    if (value === undefined) {
      setInternalValue(nextValue)
    }

    onChange?.(nextValue)
    setIsOpen(false)
    triggerRef.current?.focus()
  }

  function handleClear() {
    if (value === undefined) {
      setInternalValue('')
    }

    onChange?.('')
    onClear?.()
    setIsOpen(false)
    triggerRef.current?.focus()
  }

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (disabled) {
      return
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault()

      if (!isOpen) {
        openListbox()
        return
      }

      setActiveIndex((index) => Math.min(index + 1, options.length - 1))
      return
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()

      if (!isOpen) {
        openListbox(options.length - 1)
        return
      }

      setActiveIndex((index) => Math.max(index - 1, 0))
      return
    }

    if (event.key === 'Home') {
      event.preventDefault()
      openListbox(0)
      return
    }

    if (event.key === 'End') {
      event.preventDefault()
      openListbox(options.length - 1)
      return
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()

      if (!isOpen) {
        openListbox()
        return
      }

      const activeOption = options[activeIndex]

      if (activeOption) {
        selectOption(activeOption.value)
      }

      return
    }

    if (event.key === 'Escape') {
      event.preventDefault()
      setIsOpen(false)
    }
  }

  function handleBlur(event: FocusEvent<HTMLDivElement>) {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      setIsOpen(false)
    }
  }

  return (
    <div
      ref={rootRef}
      className={`relative w-full min-w-0 text-left ${className}`}
      onBlur={handleBlur}
      {...props}
    >
      <div
        className={`relative h-12 w-full rounded-xl border bg-white-50 text-white-950 transition-shadow focus-within:border-[#F1BD03] focus-within:ring-2 focus-within:ring-lightning-yellow-600 focus-within:ring-offset-2 ${
          hasValue
            ? 'border-[#F1BD03] shadow-[0_0_8px_#E8FF737A]'
            : 'border-transparent'
        } ${disabled ? 'opacity-70' : ''}`}
      >
        <span
          id={labelId}
          className={`pointer-events-none absolute left-5 truncate text-left font-normal transition-all ${
            hasValue
              ? 'right-20 top-2 text-xs leading-3'
              : 'right-12 top-1/2 -translate-y-1/2 text-base leading-5'
          }`}
        >
          {label}
          {required ? (
            <span aria-hidden="true" className="text-[#CA0E0E]">
              {' '}
              *
            </span>
          ) : null}
        </span>
        <button
          ref={triggerRef}
          id={fieldId}
          type="button"
          role="combobox"
          aria-activedescendant={activeOptionId}
          aria-controls={listboxId}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-labelledby={labelId}
          aria-required={required || undefined}
          disabled={disabled}
          className={`h-full w-full cursor-pointer rounded-xl bg-transparent px-5 pr-20 text-left font-sans text-sm leading-5 text-white-950 outline-none disabled:cursor-not-allowed ${
            hasValue ? 'pb-2 pt-5' : 'py-3'
          }`}
          onClick={() => {
            if (isOpen) {
              setIsOpen(false)
            } else {
              openListbox()
            }
          }}
          onKeyDown={handleKeyDown}
        >
          <span className="block truncate">
            {selectedOption?.label}
          </span>
        </button>
        {name ? <input type="hidden" name={name} value={currentValue} /> : null}
        {hasValue && !disabled ? (
          <button
            type="button"
            className="absolute right-8 top-1/2 flex size-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full text-white-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-lightning-yellow-600 focus-visible:ring-offset-2"
            aria-label={`Limpar ${label}`}
            onClick={handleClear}
          >
            <span className="flex size-6 items-center justify-center rounded-full bg-lightning-yellow-500">
              <Image
                src="/assets/icons/x.svg"
                alt=""
                width={16}
                height={16}
                aria-hidden="true"
                className="size-4"
              />
            </span>
          </button>
        ) : null}
        <Image
          src={
            isOpen
              ? '/assets/icons/caret-up.svg'
              : '/assets/icons/caret-down.svg'
          }
          alt=""
          width={24}
          height={24}
          aria-hidden="true"
          className="pointer-events-none absolute right-5 top-1/2 size-5 -translate-y-1/2 text-white-950"
        />
      </div>
      {isOpen ? (
        <ul
          id={listboxId}
          role="listbox"
          aria-labelledby={labelId}
          className="absolute left-0 right-0 z-10 mt-2 max-h-72 min-w-full overflow-auto rounded-xl bg-white-50 font-sans text-base text-white-950"
        >
          {options.map((option, index) => (
            <li
              key={option.value}
              id={`${fieldId}-option-${index}`}
              role="option"
              aria-selected={option.value === currentValue}
              className={`cursor-pointer border-b border-white-100 p-3 last:border-b-0 ${
                index === activeIndex ? 'bg-lightning-yellow-600' : ''
              }`}
              onMouseEnter={() => setActiveIndex(index)}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => selectOption(option.value)}
            >
              {option.label}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
